import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import ExpenseCard from "../../components/expenses/ExpenseCard";
import StickyHeading from "../../components/common/StickyHeading";
import {
  Sparkles,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { formatINR } from "../../lib/expenseOptions";
import { currentMonthIST, monthRangeIST } from "../../lib/datetime";
import { joinParts, plural } from "../../lib/vocabulary";

export default function TeamExpensesPage() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [allMonth, setAllMonth] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMode, setActionMode] = useState(null);
  const [remark, setRemark] = useState("");
  const [acting, setActing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      // The month is the COMPANY's, not the viewer's. Built from
      // `getFullYear/getMonth` this was the browser's local month, so on the
      // 1st of a month a phone west of IST asked the server for the previous
      // month's expenses and called the answer "this month".
      const { year, month } = currentMonthIST();
      const startIso = monthRangeIST(year, month).first;
      const [{ data: list }, { data: monthAll }, { data: users }] = await Promise.all([
        api.get("/expenses"),
        api.get("/expenses", { params: { from_date: startIso } }),
        api.get("/users"),
      ]);
      setExpenses(list);
      setAllMonth(monthAll);
      setTeam(users.filter((u) => u.role === "sales_officer" && u.is_active && u.manager_id === user?.id));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user?.id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  async function submitAction() {
    if (!actionMode) return;
    if (actionMode.kind === "reject" && !remark.trim()) {
      toast.error("Please add a reason for rejection.");
      return;
    }
    setActing(true);
    try {
      await api.put(`/expenses/${actionMode.expense.id}/${actionMode.kind}`, {
        remark: remark.trim() || null,
      });
      toast.success(actionMode.kind === "approve" ? "Approved" : "Rejected");
      setActionMode(null);
      setRemark("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  // Own submissions are visible here (a manager files too) but are NOT an
  // approval task — they belong on the Mine tab, and nobody approves their own.
  const pending = useMemo(
    () => expenses.filter((e) => e.status === "Submitted" && e.sales_officer_id !== user?.id),
    [expenses, user?.id]
  );
  const decided = useMemo(() => expenses.filter((e) => e.status !== "Submitted" && e.status !== "Draft"), [expenses]);

  // Everyone this viewer approves for — their Sales Officer reports (shown even
  // at ₹0) plus anyone else whose claims reached their queue. Building it from
  // SO reports alone left the Admin Manager with an empty roster and a "0 sales
  // officers in your team" subtitle, while managers' claims went unreviewed.
  const summary = useMemo(() => {
    const map = {};
    for (const m of team) {
      map[m.id] = { id: m.id, name: m.full_name, claimed: 0, approved: 0 };
    }
    for (const e of allMonth) {
      if (e.sales_officer_id === user?.id) continue; // own claims live on Mine
      const row = (map[e.sales_officer_id] ||= {
        id: e.sales_officer_id,
        name: e.sales_officer_name || "Unknown",
        claimed: 0,
        approved: 0,
      });
      row.claimed += e.amount;
      if (e.status === "Approved") row.approved += e.amount;
    }
    return Object.values(map).sort((a, b) => b.claimed - a.claimed);
  }, [team, allMonth, user?.id]);

  return (
    <div className="space-y-6" data-testid="team-expenses-page">
      <StickyHeading
        icon={Sparkles}
        label="Team Expense Approvals"
        title="Team expenses"
        subtitle={joinParts([
          plural(pending.length, "pending approval"),
          `${plural(summary.length, "person", "people")} you approve for`,
        ])}
        testId="team-expenses-heading"
      />

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {/* Per-SO month summary */}
      <section>
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
          This month · Claimed vs Approved
        </h2>
        {summary.length === 0 ? (
          <div className="bg-white border border-callisto rounded-xl p-6 text-center text-sm text-callisto-muted">
            No team members yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="team-expenses-summary">
            {summary.map((s) => (
              <div key={s.id} className="bg-white border border-callisto rounded-xl p-4">
                <p className="text-sm font-semibold text-callisto-navy truncate">{s.name}</p>
                <p className="mt-1 text-[11px] uppercase tracking-wider text-callisto-muted">
                  Claimed{" "}
                  <span className="font-bold text-callisto-navy ml-0.5">{formatINR(s.claimed)}</span>
                </p>
                <p className="text-[11px] uppercase tracking-wider text-callisto-muted">
                  Approved{" "}
                  <span className="font-bold text-callisto-gold ml-0.5">{formatINR(s.approved)}</span>
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Pending */}
      <section>
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
          Pending approval · {pending.length}
        </h2>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        ) : pending.length === 0 ? (
          <div className="bg-white border border-callisto rounded-xl p-6 text-center text-sm text-callisto-muted">
            All caught up — no pending expenses.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="team-expenses-pending">
            {pending.map((e) => (
              <ExpenseCard
                key={e.id}
                expense={e}
                showOfficer
                action={
                  // can_approve comes from the backend's approval rule, so a
                  // submitter never sees these on their own claim.
                  !e.can_approve ? null : (
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      onClick={() => setActionMode({ expense: e, kind: "approve" })}
                      className="h-7 px-2.5 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`team-expenses-approve-${e.id}`}
                    >
                      <CheckCircle2 size={12} className="mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setActionMode({ expense: e, kind: "reject" })}
                      className="h-7 px-2.5 text-[11px] border-red-200 text-red-700 hover:bg-red-50"
                      data-testid={`team-expenses-reject-${e.id}`}
                    >
                      <XCircle size={12} className="mr-1" />
                      Reject
                    </Button>
                  </div>
                  )
                }
              />
            ))}
          </div>
        )}
      </section>

      {/* Decided */}
      <section>
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
          Decided · {decided.length}
        </h2>
        {decided.length === 0 ? (
          <div className="bg-white border border-callisto rounded-xl p-6 text-center text-sm text-callisto-muted">
            No decisions yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {decided.map((e) => (
              <ExpenseCard key={e.id} expense={e} showOfficer />
            ))}
          </div>
        )}
      </section>

      <Dialog
        open={!!actionMode}
        onOpenChange={(o) => {
          if (!o) {
            setActionMode(null);
            setRemark("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionMode?.kind === "approve" ? "Approve expense" : "Reject expense"}
            </DialogTitle>
            <DialogDescription>
              {joinParts([
                actionMode?.expense?.sales_officer_name,
                actionMode?.expense?.expense_type,
                actionMode?.expense && formatINR(actionMode.expense.amount),
              ])}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={3}
            placeholder={actionMode?.kind === "approve" ? "Optional remark" : "Reason for rejection (required)"}
            className="focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="team-expenses-remark"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionMode(null);
                setRemark("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={submitAction}
              disabled={acting}
              className={
                actionMode?.kind === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"
              }
              data-testid="team-expenses-action-confirm"
            >
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : actionMode?.kind === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
